export interface VisibleRange {
  count: number; offset: number;
  start: number; end: number;          // integer candle indices actually present (clamped to the series)
  startFrac: number; endFrac: number;  // fractional indices at the left / right plot edges (may exceed N-1 = future space)
}

const MIN_VISIBLE = 20;
const DEFAULT_COUNT = 120;

// Which slice of the series is on screen. `offset` = bars between the newest candle and the right edge;
// negative offsets are genuinely EMPTY future space (no fabricated candles). `isLiveFollowing` is explicit
// state: true → new candles keep the newest bar at the live edge, false → viewport stays where the user left it.
export class Viewport {
  count = DEFAULT_COUNT;
  offset = 0;
  isLiveFollowing = true;
  futureFraction = 0.10;     // default live margin (share of the visible width) — configurable
  maxFutureFraction = 0.35;  // how far the user may pan into the future

  futureBars(count = this.count) { return Math.round(count * this.futureFraction); }
  liveOffset(count = this.count) { return -this.futureBars(count); }
  minOffset(count = this.count) { return -Math.round(count * this.maxFutureFraction); }

  clampCount(count: number, N: number) { return Math.max(MIN_VISIBLE, Math.min(Math.round(count), Math.max(MIN_VISIBLE, N))); }
  clampOffset(offset: number, N: number, count = this.count) {
    return Math.max(this.minOffset(count), Math.min(offset, Math.max(0, N - MIN_VISIBLE)));
  }

  range(N: number): VisibleRange {
    const count = this.clampCount(this.count, N);
    let offset = this.clampOffset(this.offset, N, count);
    if (this.isLiveFollowing) offset = Math.min(offset, this.liveOffset(count));
    const endFrac = N - 1 - offset;
    const startFrac = endFrac - count + 1;
    return { count, offset, startFrac, endFrac, start: Math.max(0, Math.floor(startFrac)), end: Math.min(N - 1, Math.ceil(endFrac)) };
  }

  // Zoom keeping the candle under the cursor (anchorIdx at anchorFrac of the plot width) fixed; at the live edge keep the margin.
  zoomAt(factor: number, N: number, anchorIdx?: number, anchorFrac?: number) {
    this.count = this.clampCount(this.clampCount(this.count, N) * factor, N);
    const count = this.clampCount(this.count, N);
    if (this.isLiveFollowing || anchorIdx == null || anchorFrac == null) { this.offset = Math.min(this.offset, this.liveOffset(count)); if (this.isLiveFollowing) this.offset = this.liveOffset(count); return; }
    const startFrac = anchorIdx - anchorFrac * count + 0.5;
    this.offset = this.clampOffset(N - 1 - (startFrac + count - 1), N, count);
  }

  zoom(factor: number, N: number) { this.zoomAt(factor, N); }

  // Called after every user-driven move: reaching the live edge resumes following, leaving it stops following.
  setOffset(offset: number, N: number) {
    const count = this.clampCount(this.count, N);
    this.offset = this.clampOffset(offset, N, count);
    this.isLiveFollowing = this.offset <= this.liveOffset(count) + 0.5;
  }

  panBy(bars: number, N: number) { this.setOffset(this.offset + bars, N); }

  goLive(N: number) {
    this.offset = this.liveOffset(this.clampCount(this.count, N));
    this.isLiveFollowing = true;
  }

  reset() {
    this.count = DEFAULT_COUNT;
    this.offset = this.liveOffset(DEFAULT_COUNT);
    this.isLiveFollowing = true;
  }
}
